<template>
  <v-alert
    v-model="show"
    closable
    :type="alert?.alertType"
    class="ma-1"
    @click:close="handleAlertClosed"
  >
    <div v-html="renderedMarkdown"></div>
  </v-alert>
</template>
<script setup>
import { ref, watch, computed } from 'vue';
import { useRootStore } from '../../../stores/root';
import { storeToRefs } from 'pinia';
import { getAPI } from './supportAlerts';
import { useRoute } from 'vue-router';
import { debounce } from 'lodash';
import MarkdownIt from "markdown-it";

const MAX_ACKNOWLEDGED_PAGE_ALERT_KEYS = 30;

const md = new MarkdownIt();
const show = ref(false);
const alert = ref();

const store = useRootStore();
const { orgId, enterpriseClient, userId } = storeToRefs(store);
const route = useRoute();

const renderedMarkdown = computed(() => {
  return md.render(alert.value?.text || "");
});

function addBaseToRoute(path) {
  // strip the last / from the base url, so /admin/ => /admin, /admin => /admin, / => /
  const stripped = import.meta.env.BASE_URL.replace(/\/$/, '');
  return import.meta.env.BASE_URL === '/' ? path : `${stripped}${path}`;
}

const acknowledgedAlerts = ref(JSON.parse(localStorage.getItem('acknowledgedAlerts')) || []);
const acknowledgedAlertsComputed = computed({
  get: () => acknowledgedAlerts.value,
  set: v => {
    // remove old keys
    if (v.length > MAX_ACKNOWLEDGED_PAGE_ALERT_KEYS) {
      v = v.slice(v.length - MAX_ACKNOWLEDGED_PAGE_ALERT_KEYS);
    }
    acknowledgedAlerts.value = v;
    localStorage.setItem('acknowledgedAlerts', JSON.stringify(v));
  }
})

function getAckKey(alert) {
  return `${alert.PK}-${alert.SK}`;
}

async function loadPageAlert() {
  try {
    const pageAlertsApiInput = {
      userId: userId.value,
      orgId: orgId.value,
      enterprise: enterpriseClient.value,
      page: addBaseToRoute(route.path),
    }
    let pageAlerts = await getAPI(pageAlertsApiInput);
    pageAlerts = pageAlerts.filter(x => !acknowledgedAlertsComputed.value.includes(getAckKey(x)));
    alert.value = pageAlerts[0];
    show.value = !!alert.value;
  } catch (e) {
    console.error('failed to fetch support alert', e);
    show.value = false;
  }
}

function handleAlertClosed() {
  show.value = false;
  if (alert.value?.alertType === 'error') return; // dont acknowledge errors
  acknowledgedAlertsComputed.value = [...acknowledgedAlertsComputed.value, getAckKey(alert.value)];
}

const debounceLoadPageAlert = debounce(loadPageAlert, 1000);
watch(() => route.path, (newValue, oldValue) => {
  if (newValue === oldValue) return;
  show.value = false;
  debounceLoadPageAlert();
}, { immediate: true });

</script>
<style scoped>
:deep(ul), :deep(ol) {
  padding-inline-start: 40px;
}
</style>

